import { Box, Typography } from '@mui/material';
import { splitLabelled } from '../../content';
import { tokens } from '../../theme';
import type { StepListProps } from './types';

/**
 * The chronological path through a method — `1. Define Objectives` and what
 * that means, then the next.
 *
 * **The numbers are the component's.** They are literals inside the Figma text
 * nodes, which makes them look like content; the API returns clean strings, and
 * a step arriving already numbered would render as "1. 1. Define Objectives".
 * This is an `ol`, so the order is in the markup too and the printed number is
 * decoration on top of it.
 *
 * **A step is split on its first colon, the same rule as the mastery ladder.**
 * The designs draw a bold title over a grey explanation, and where a step is
 * one sentence with no colon it becomes the title alone — which is the shape
 * the mock content has, so this degrades to a plain numbered list rather than
 * to a column of headings with nothing under them.
 */
export const StepList = ({ steps }: StepListProps) => {
  if (steps.length === 0) {
    return null;
  }

  return (
    <Box
      component="ol"
      sx={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {steps.map((step, index) => {
        const { label, description } = splitLabelled(step);

        return (
          <Box component="li" key={step}>
            <Typography
              component="p"
              sx={{
                fontFamily: tokens.fontFamily.heading,
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1.4,
                color: 'text.primary',
              }}
            >
              {index + 1}. {label}
            </Typography>

            {description !== undefined && (
              <Typography variant="body2" sx={{ color: 'text.secondary', marginTop: '2px' }}>
                {description}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
